import { atom } from "jotai";
import { Metric, Value } from "@/entities/Finance/Finance.model";
import { QueryAtom } from "@/api/types/query.types";
import { AccountLabel, PeriodKind } from "@/entities/Labeling/Account.model";
import { DataSource } from "./hooks/useColorizedValue";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";
import {
    BALANCE_SHEET_LABELS,
    INCOME_STATEMENT_LABELS,
    POST_CLOSING_METRICS_LABELS,
    PROJECTED_FINANCIALS_METRICS_LABELS,
    SUMMARY_INCOME_STATEMENT_METRIC_PAIRS,
    SUMMARY_BALANCE_SHEET_METRIC_PAIRS,
} from "./metrics.const";
import { parseMetricLineage } from "@/apps/AdminBetaApp/sections/companies/MetricsLab/parseMetricExpression";
import dayjs from "dayjs";

const getMaxAllowedDate = (): string => {
    const now = dayjs();
    const twoMonthsAgo = now.subtract(2, "month");
    return twoMonthsAgo.endOf("month").format("YYYY-MM-DD");
};

export const incomeStatementResolutionAtom = atom<{
    periodKind: PeriodKind;
    window?: string;
}>({
    periodKind: PeriodKind.MONTHLY,
    window: "l12m",
});

export const balanceSheetResolutionAtom = atom<{
    periodKind: PeriodKind;
    window?: string;
}>({
    periodKind: PeriodKind.MONTHLY,
    window: "l12m",
});

export const projectionsResolutionAtom = atom<{
    periodKind: PeriodKind;
    window?: string;
}>({
    periodKind: PeriodKind.MONTHLY,
    window: "l12m",
});

export const metricResolutionAtom = incomeStatementResolutionAtom;

export const financeMonthlyQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const financeQuarterlyQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const financeYearlyQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

// Finance Lab queries
export const finLabMonthlyQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const finLabQuarterlyQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const finLabYearlyQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const finLabProjectionsQueryAtom = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

// Balance Sheet specific query atoms
export const financeMonthlyQueryAtomBS = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const financeQuarterlyQueryAtomBS = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const financeYearlyQueryAtomBS = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const finLabMonthlyQueryAtomBS = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const finLabQuarterlyQueryAtomBS = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const finLabYearlyQueryAtomBS = atom<QueryAtom<Metric[]>>({
    data: null,
    isLoading: false,
    isError: false,
});

export const financeMonthlyMetricsAtomBS = atom((get) =>
    normalizeLineageMetrics(
        get(financeMonthlyQueryAtomBS),
        DataSource.FINANCE_API,
    ),
);

export const financeQuarterlyMetricsAtomBS = atom((get) =>
    normalizeLineageMetrics(
        get(financeQuarterlyQueryAtomBS),
        DataSource.FINANCE_API,
    ),
);

export const financeYearlyMetricsAtomBS = atom((get) =>
    normalizeLineageMetrics(
        get(financeYearlyQueryAtomBS),
        DataSource.FINANCE_API,
    ),
);

export const finLabMonthlyMetricsAtomBS = atom((get) =>
    normalizeMetrics(get(finLabMonthlyQueryAtomBS), DataSource.FINANCE_LAB),
);

export const finLabQuarterlyMetricsAtomBS = atom((get) =>
    normalizeMetrics(get(finLabQuarterlyQueryAtomBS), DataSource.FINANCE_LAB),
);

export const finLabYearlyMetricsAtomBS = atom((get) =>
    normalizeMetrics(get(finLabYearlyQueryAtomBS), DataSource.FINANCE_LAB),
);

function calculateLastTwoPeriodsDeltas(values: Value[]): number | null {
    if (values.length < 2) return null;
    const sorted = [...values].sort((a, b) => a.period.localeCompare(b.period));
    const lastValue = sorted[sorted.length - 1].value;
    const secondLastValue = sorted[sorted.length - 2].value;
    if (secondLastValue === 0) return null;
    const delta = (lastValue - secondLastValue) / secondLastValue;
    return delta === 0 ? 0 : delta;
}

function calculateCumulativeDelta(values: Value[]): number | null {
    if (values.length < 2) return null;

    const sorted = [...values].sort((a, b) => a.period.localeCompare(b.period));
    const last12 = sorted.slice(-12);

    if (last12.length < 2) return null;

    let totalDelta = 0;
    let comparisons = 0;

    for (let i = 1; i < last12.length; i++) {
        const previousValue = last12[i - 1].value;
        if (previousValue === 0) continue;

        const currentValue = last12[i].value;
        const periodDelta = (currentValue - previousValue) / previousValue;
        totalDelta += periodDelta;
        comparisons++;
    }

    return comparisons > 0 ? totalDelta : null;
}

function isBalanceSheetMetric(label: string): boolean {
    return BALANCE_SHEET_LABELS.has(label as AccountLabel | FinanceLabField);
}

function addDeltaToMetric(metric: Metric, source: DataSource): Metric {
    const isBalanceSheet = isBalanceSheetMetric(metric.info.derived.label);
    const delta = isBalanceSheet
        ? calculateLastTwoPeriodsDeltas(metric.values)
        : calculateCumulativeDelta(metric.values);

    return {
        ...metric,
        delta,
        source,
    };
}

function convertLineageMetricToMetric(
    lineageMetric: Metric,
    source: DataSource,
): Metric {
    const values: Value[] =
        lineageMetric.lineagedValues?.map((lv: any) => ({
            period: lv.period,
            value: lv.value,
        })) || [];

    const isBalanceSheet = isBalanceSheetMetric(
        lineageMetric.info.derived.label,
    );
    const delta = isBalanceSheet
        ? calculateLastTwoPeriodsDeltas(values)
        : calculateCumulativeDelta(values);

    const parsedMetrics =
        lineageMetric.lineagedValues && lineageMetric.lineagedValues.length > 0
            ? parseMetricLineage({
                  metrics: {
                      [lineageMetric.info.derived.label]: [lineageMetric],
                  },
              })
            : [];

    return {
        info: lineageMetric.info,
        description: lineageMetric.description,
        values,
        lineagedValues: parsedMetrics,
        delta,
        source,
    };
}

function filterMetricsByLabels(
    metrics: Record<string, Metric>,
    labels: string[],
): Record<string, Metric> {
    return Object.fromEntries(
        labels
            .map((label) => [label, metrics[label]])
            .filter(([, metric]) => metric !== undefined),
    );
}

const normalizeMetrics = (query: QueryAtom<Metric[]>, source: DataSource) => {
    if (!query.data) return {};
    const result: Record<string, Metric> = {};
    for (const metric of query.data) {
        result[metric.info.derived.label] = addDeltaToMetric(metric, source);
    }
    return result;
};

const normalizeLineageMetrics = (
    query: QueryAtom<Metric[]>,
    source: DataSource,
) => {
    if (!query.data) return {};
    const result: Record<string, Metric> = {};
    for (const lineageMetric of query.data) {
        result[lineageMetric.info.derived.label] = convertLineageMetricToMetric(
            lineageMetric,
            source,
        );
    }
    return result;
};

export const financeMonthlyMetricsAtom = atom((get) =>
    normalizeLineageMetrics(
        get(financeMonthlyQueryAtom),
        DataSource.FINANCE_API,
    ),
);

export const financeQuarterlyMetricsAtom = atom((get) =>
    normalizeLineageMetrics(
        get(financeQuarterlyQueryAtom),
        DataSource.FINANCE_API,
    ),
);

export const financeYearlyMetricsAtom = atom((get) =>
    normalizeLineageMetrics(
        get(financeYearlyQueryAtom),
        DataSource.FINANCE_API,
    ),
);

export const finLabMonthlyMetricsAtom = atom((get) =>
    normalizeMetrics(get(finLabMonthlyQueryAtom), DataSource.FINANCE_LAB),
);

export const finLabQuarterlyMetricsAtom = atom((get) =>
    normalizeMetrics(get(finLabQuarterlyQueryAtom), DataSource.FINANCE_LAB),
);

export const finLabYearlyMetricsAtom = atom((get) =>
    normalizeMetrics(get(finLabYearlyQueryAtom), DataSource.FINANCE_LAB),
);

export const finLabProjectionsMetricsAtom = atom((get) =>
    normalizeMetrics(get(finLabProjectionsQueryAtom), DataSource.FINANCE_LAB),
);

export const selectedPeriodKindAtom = atom<PeriodKind>(PeriodKind.MONTHLY);

export const activeFinanceMetricsAtom = atom<Record<string, Metric>>((get) => {
    const { periodKind } = get(incomeStatementResolutionAtom);

    const periodMap = {
        [PeriodKind.MONTHLY]: financeMonthlyMetricsAtom,
        [PeriodKind.QUARTERLY]: financeQuarterlyMetricsAtom,
        [PeriodKind.YEARLY]: financeYearlyMetricsAtom,
    };

    return get(periodMap[periodKind] ?? financeMonthlyMetricsAtom);
});

export const activeFinLabMetricsAtom = atom<Record<string, Metric>>((get) => {
    const { periodKind } = get(incomeStatementResolutionAtom);

    const periodMap = {
        [PeriodKind.MONTHLY]: finLabMonthlyMetricsAtom,
        [PeriodKind.QUARTERLY]: finLabQuarterlyMetricsAtom,
        [PeriodKind.YEARLY]: finLabYearlyMetricsAtom,
    };

    return get(periodMap[periodKind] ?? finLabMonthlyMetricsAtom);
});

export const activeFinanceMetricsAtomBS = atom<Record<string, Metric>>(
    (get) => {
        const { periodKind } = get(balanceSheetResolutionAtom);

        const periodMap = {
            [PeriodKind.MONTHLY]: financeMonthlyMetricsAtomBS,
            [PeriodKind.QUARTERLY]: financeQuarterlyMetricsAtomBS,
            [PeriodKind.YEARLY]: financeYearlyMetricsAtomBS,
        };

        return get(periodMap[periodKind] ?? financeMonthlyMetricsAtomBS);
    },
);

export const activeFinLabMetricsAtomBS = atom<Record<string, Metric>>((get) => {
    const { periodKind } = get(balanceSheetResolutionAtom);

    const periodMap = {
        [PeriodKind.MONTHLY]: finLabMonthlyMetricsAtomBS,
        [PeriodKind.QUARTERLY]: finLabQuarterlyMetricsAtomBS,
        [PeriodKind.YEARLY]: finLabYearlyMetricsAtomBS,
    };

    return get(periodMap[periodKind] ?? finLabMonthlyMetricsAtomBS);
});
export const unifiedMetricsAtom = atom<Record<string, Metric>>((get) => {
    const financeMetrics = get(activeFinanceMetricsAtom);
    const finLabMetrics = get(activeFinLabMetricsAtom);
    const projectionMetrics = get(finLabProjectionsMetricsAtom);

    return {
        ...finLabMetrics,
        ...projectionMetrics,
        ...financeMetrics,
    };
});

function filterMetricsByMaxDate(
    metrics: Record<string, Metric>,
): Record<string, Metric> {
    const maxDate = getMaxAllowedDate();
    const filtered: Record<string, Metric> = {};

    for (const [key, metric] of Object.entries(metrics)) {
        const filteredValues = metric.values.filter((v) => v.period <= maxDate);

        const isBalanceSheet = isBalanceSheetMetric(metric.info.derived.label);
        const delta = isBalanceSheet
            ? calculateLastTwoPeriodsDeltas(filteredValues)
            : calculateCumulativeDelta(filteredValues);

        filtered[key] = {
            ...metric,
            values: filteredValues,
            delta,
        };
    }
    return filtered;
}

export const incomeStatementMetricsAtom = atom<Record<string, Metric>>(
    (get) => {
        const financeMetrics = get(activeFinanceMetricsAtom);
        const finLabMetrics = get(activeFinLabMetricsAtom);

        const unified = {
            ...finLabMetrics,
            ...financeMetrics,
        };

        const labels = Array.from(INCOME_STATEMENT_LABELS);
        const labelFiltered = filterMetricsByLabels(unified, labels);

        return filterMetricsByMaxDate(labelFiltered);
    },
);

export const balanceSheetMetricsAtom = atom<Record<string, Metric>>((get) => {
    // Use Balance Sheet specific metrics atoms
    const financeMetrics = get(activeFinanceMetricsAtomBS);
    const finLabMetrics = get(activeFinLabMetricsAtomBS);

    const unified = {
        ...finLabMetrics,
        ...financeMetrics,
    };

    const labels = Array.from(BALANCE_SHEET_LABELS);
    const labelFiltered = filterMetricsByLabels(unified, labels);

    return filterMetricsByMaxDate(labelFiltered);
});

export const projectedFinancialsMetricsAtom = atom<Record<string, Metric>>(
    (get) => {
        const projectionMetrics = get(unifiedMetricsAtom);

        const labels = Array.from(PROJECTED_FINANCIALS_METRICS_LABELS);

        return filterMetricsByLabels(projectionMetrics, labels);
    },
);

export const postClosingMetricsAtom = atom<Record<string, Metric>>((get) => {
    const postClosingMetrics = get(unifiedMetricsAtom);

    const labels = Array.from(POST_CLOSING_METRICS_LABELS);

    return filterMetricsByLabels(postClosingMetrics, labels);
});

function getLatestPeriodFromMetrics(metrics: Metric[]): string | null {
    let latestPeriod: string | null = null;

    metrics.forEach((metric) => {
        const validValues = metric.values.filter(
            (v) => v.value !== null && v.value !== undefined,
        );

        if (validValues.length > 0) {
            const sortedValues = [...validValues].sort((a, b) =>
                b.period.localeCompare(a.period),
            );
            const metricLatest = sortedValues[0].period;

            if (!latestPeriod || metricLatest > latestPeriod) {
                latestPeriod = metricLatest;
            }
        }
    });

    return latestPeriod;
}

export const projectionsAsOfDateAtom = atom<string | null>((get) => {
    const projectionMetrics = get(finLabProjectionsMetricsAtom);
    return getLatestPeriodFromMetrics(Object.values(projectionMetrics));
});

function getPeriodsFromMetricPairs(
    metrics: Record<string, Metric>,
    metricPairs: Array<[string, string | undefined]>,
): string[] {
    const periods = new Set<string>();

    for (const [primary, fallback] of metricPairs) {
        const metric =
            metrics[primary] ?? (fallback ? metrics[fallback] : undefined);
        metric?.values.forEach((v) => periods.add(v.period));
    }

    return Array.from(periods).sort();
}

export const summaryIncomeStatementEarliestDateAtom = atom<string | null>(
    (get) => {
        const monthlyFinance = get(financeMonthlyMetricsAtom);
        const monthlyFinLab = get(finLabMonthlyMetricsAtom);

        const combined = { ...monthlyFinLab, ...monthlyFinance };
        const filtered = filterMetricsByMaxDate(combined);
        const periods = getPeriodsFromMetricPairs(
            filtered,
            SUMMARY_INCOME_STATEMENT_METRIC_PAIRS,
        );

        return periods[0] ?? null;
    },
);

export const summaryIncomeStatementAsOfDateAtom = atom<string | null>((get) => {
    const monthlyFinance = get(financeMonthlyMetricsAtom);
    const monthlyFinLab = get(finLabMonthlyMetricsAtom);

    const combined = { ...monthlyFinLab, ...monthlyFinance };
    const filtered = filterMetricsByMaxDate(combined);
    const periods = getPeriodsFromMetricPairs(
        filtered,
        SUMMARY_INCOME_STATEMENT_METRIC_PAIRS,
    );

    return periods[periods.length - 1] ?? null;
});

export const summaryBalanceSheetEarliestDateAtom = atom<string | null>(
    (get) => {
        const monthlyFinance = get(financeMonthlyMetricsAtomBS);
        const monthlyFinLab = get(finLabMonthlyMetricsAtomBS);

        const combined = { ...monthlyFinLab, ...monthlyFinance };
        const filtered = filterMetricsByMaxDate(combined);
        const periods = getPeriodsFromMetricPairs(
            filtered,
            SUMMARY_BALANCE_SHEET_METRIC_PAIRS,
        );

        return periods[0] ?? null;
    },
);

export const summaryBalanceSheetAsOfDateAtom = atom<string | null>((get) => {
    const monthlyFinance = get(financeMonthlyMetricsAtomBS);
    const monthlyFinLab = get(finLabMonthlyMetricsAtomBS);

    const combined = { ...monthlyFinLab, ...monthlyFinance };
    const filtered = filterMetricsByMaxDate(combined);
    const periods = getPeriodsFromMetricPairs(
        filtered,
        SUMMARY_BALANCE_SHEET_METRIC_PAIRS,
    );

    return periods[periods.length - 1] ?? null;
});

export const financialHighlightsAsOfDateAtom = atom<string | null>((get) => {
    const isDate = get(summaryIncomeStatementAsOfDateAtom);
    const bsDate = get(summaryBalanceSheetAsOfDateAtom);

    if (!isDate && !bsDate) return null;
    if (!isDate) return bsDate;
    if (!bsDate) return isDate;

    return isDate < bsDate ? isDate : bsDate;
});
